import React from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import { Redirect, withRouter } from 'core/libs/router';

import resolveRelationships from 'core/utils/relationships';
import {
  pageLinkConstructor,
  checkPageUrlParam,
} from 'core/utils/url-helper';
import pushwooshPropTypes from 'core/utils/prop-types/pushwoosh';
import { resolve as resolveEnv } from 'core/utils/env';

import { END_OF_TOPIC_CONTENT } from 'core/components/TopicContent/constants';
import RcmEventsSender from 'core/components/RcmEventsSender';
import EmptyWrapper from 'core/components/EmptyWrapper';
import { initPushWoosh } from 'core/components/PushWoosh';
import ScrollToSite, { checkScrollToSiteInTopic } from 'core/components/ScrollToSite';


export const TopicContext = React.createContext(null);

const relationships = resolveRelationships(
  ['content', 'scroll_to_site'],
  {},
  { content: { widgets: [] } },
);


/**
 * Базовое отображение топика.
 * Формирует метатеги для топика и вызывает функцию для отрисовки.
 *
 * @author Anton Ignatov <a.ignatov@rambler-co.ru>
 */
class BaseTopic extends React.Component {
  static propTypes = {
    /**
     * Функция в которую будет передан результат запроса данных.
     * Функция должна вернуть реактовский элемент для отрисовки или `null`.
     */
    children: PropTypes.func.isRequired,

    /**
     * Необходимо, чтобы при переходе из статичных блоков к топикам
     * без бесконечного скролла происходило перемаунчивание, и
     * события RecSys отправляли корректно
     */
    key: PropTypes.string,

    /**
     * Данные топика со всеми зависимостями `include=all`
     */
    content: PropTypes.object.isRequired,

    /** @ignore */
    location: PropTypes.object,

    /** Номер топика в бесконечном скролле. */
    infinityIndex: PropTypes.number,

    /** id блока рекомендации */
    rcmBlockId: PropTypes.string,

    /** id первого топика (не из блока рекоммендаций) */
    contextItemId: PropTypes.string,

    /**
     * `withRequestId` здесь использовать нельзя, т.к это приведет
     * к тому, что компонент перерендерится раньше чем Topic и со старым
     * контентом
     */
    requestId: PropTypes.string,

    /** sessionId возвращенный с первым запросом к fulltext блоку рекомендаций */
    rcmSessionId: PropTypes.string,
  };

  static contextTypes = {
    host: PropTypes.string,
    pushWoosh: pushwooshPropTypes,
  };

  static childContextTypes = {
    webdavHost: PropTypes.string,
    thumborHost: PropTypes.string,
  };

  static defaultProps = {
    /**
     * children здесь необходим, т.к этот компонент используется
     * в менеджере загрузок. При резолвинге дата провайдеров эти компоненты
     * отрисовываться без каких-либо пропсов и children(props.content)
     * вызовет ошибку.
     * Подробнее тут: https://gitlab.rambler.ru/unity/layout-builder/tree/master#как-работает-менеджер
     */
    children: () => null,
  };

  constructor(props, context) {
    super(props, context);

    this.rcmSended = false;
    this.articleRef = React.createRef();
    this.endOfArticleRef = React.createRef();
  }

  getChildContext() {
    const {
      location: { search },
    } = this.props;

    const { is_mock: isMock } = queryString.parse(search);

    const isNotProd = resolveEnv({
      '*': true,
      'production': false,
    });

    return (!!isMock && isNotProd) ? {
      webdavHost: 'quto.ru',
      thumborHost: 'https://quto.ru/thumb',
    } : {};
  }

  componentDidMount() {
    if (!process.env.BROWSER_RUNTIME) return;

    /**
     * Отправка событий при просмотре топика
     */
    this.pagereadObserverer = new IntersectionObserver((entries) => {
      const isIntersecting = entries[0].isIntersecting;
      if (isIntersecting) {
        if (!this.rcmSended) {
          this.sendRcmPageread();
          initPushWoosh(this.context.pushWoosh, 'page_read');
          this.rcmSended = true;
        }
      }
    });

    const endOfTopicContentElement = this.articleRef.current && this.articleRef.current.querySelector(`.${END_OF_TOPIC_CONTENT}`);
    const endOfTopicElement = this.endOfArticleRef.current;

    // Иногда у топика TopicContent может отсутствовать
    this.endElement = endOfTopicContentElement || endOfTopicElement;

    this.endElement && this.pagereadObserverer.observe(this.endElement);
  }

  shouldComponentUpdate(nextProps) {
    return this.props.requestId !== nextProps.requestId;
  }

  componentDidUpdate(prevProps) {
    const {
      location: {
        key: prevLocationKey,
      },
    } = prevProps;
    const pageHasChanged = !this.props.infinityIndex && (prevLocationKey !== this.props.location.key);

    if (this.pagereadObserverer && pageHasChanged) {
      this.pagereadObserverer.disconnect();
      if (this.endElement) {
        this.pagereadObserverer.observe(this.endElement);
      }
    }
  }

  componentWillUnmount() {
    this.pagereadObserverer && this.pagereadObserverer.disconnect();
  }

  sendRcmPageread = () => {
    window.rcm && window.rcm('pageread', {
      itemId: this.props.content.id,
    });
  };

  render() {
    const {
      children,
      location,
      infinityIndex,
      content,
      contextItemId,
      rcmBlockId,
      rcmSessionId,
    } = this.props;

    const {
      content: {
        widgets,
      },
      scroll_to_site: scrollToSite,
    } = relationships(content);

    if (location.search.includes('erid=')) {
      const query = queryString.parse(location.search);
      const newQuery = queryString.stringify({
        ...query,
        erid: undefined,
      });

      return <Redirect to={location.pathname + newQuery.length ? `?${newQuery}` : ''} />;
    }

    const topic = {
      content,
      infinityIndex,
      rcmSessionId,
    };

    /**
     * Делаем редирект на 1 страницу, если страница невалидна
     * При включеном бесконечном скроле делать это нужно только для
     * первого топика
     */
    if (!infinityIndex) {
      const pageCount = widgets
        .filter(widget => widget.type === 'pageBreak')
        .length + 1 || 1;

      if (!checkPageUrlParam(location, pageCount)) {
        return <Redirect to={pageLinkConstructor(1, location)} />;
      }
    }

    const blockinitParams = rcmBlockId
      ? {
        blockId: rcmBlockId,
        contextItemId,
      }
      : undefined;

    const Wrapper = infinityIndex ? EmptyWrapper : RcmEventsSender;

    return (
      <Wrapper
        pageview={{ itemId: content.id }}
        blockinit={blockinitParams}
      >
        <TopicContext.Provider value={topic}>
          <article ref={this.articleRef}>
            {children(content, topic)}
            <div ref={this.endOfArticleRef} />
          </article>
        </TopicContext.Provider>
        {checkScrollToSiteInTopic(topic) && (
          <ScrollToSite content={scrollToSite} />
        )}
      </Wrapper>
    );
  }
}

const BaseTopicWithHOCs = withRouter(BaseTopic);
BaseTopicWithHOCs.displayName = 'BaseTopic';

export { BaseTopic as StorybookComponent };
export default BaseTopicWithHOCs;
